<template>
  <div>
    <v-dialog v-model="modalStatus" max-width="620" scrollable persistent>
      <v-card class="modal-card">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-left">
            <div class="header-icon">
              <v-icon size="16" color="white">mdi-import</v-icon>
            </div>
            <span class="header-title">Importar Protocolos</span>
          </div>
          <button class="close-btn" @click="closeModal">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <v-card-text class="modal-body pa-0">

          <!-- Loading state -->
          <div v-if="loading" class="loading-state">
            <v-progress-circular indeterminate color="#5e85ef" size="32" width="3"></v-progress-circular>
            <p class="loading-text">Cargando protocolos...</p>
          </div>

          <div v-else class="content-wrapper">
            <!-- Search -->
            <div class="search-section">
              <div class="search-input-wrapper">
                <v-icon class="search-icon" size="16" color="#9ca3af">mdi-magnify</v-icon>
                <input
                  v-model="searchText"
                  type="text"
                  class="search-input"
                  placeholder="Buscar por nombre o etapa..."
                  @input="onSearchInput"
                />
                <button v-if="searchText" class="clear-btn" @click="searchText = ''; onSearchInput()">
                  <v-icon size="14" color="#9ca3af">mdi-close</v-icon>
                </button>
              </div>
              <div class="results-count" v-if="!loading">
                <span>{{ filteredProtocolos.length }} resultado{{ filteredProtocolos.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>

            <!-- Select all row -->
            <div class="select-all-row" v-if="filteredProtocolos.length > 0">
              <label class="select-all-label">
                <div
                  class="custom-checkbox"
                  :class="{
                    checked: isAllSelected,
                    indeterminate: isIndeterminate
                  }"
                  @click="toggleAll"
                >
                  <v-icon v-if="isAllSelected" size="11" color="white">mdi-check</v-icon>
                  <v-icon v-else-if="isIndeterminate" size="11" color="white">mdi-minus</v-icon>
                </div>
                <span>Seleccionar todos</span>
              </label>
              <v-chip
                v-if="selectedProtocolos.length > 0"
                x-small
                color="#5e85ef"
                text-color="white"
                class="count-chip"
              >
                {{ selectedProtocolos.length }} seleccionado{{ selectedProtocolos.length !== 1 ? 's' : '' }}
              </v-chip>
            </div>

            <!-- Protocols list with lazy loading -->
            <div
              class="protocols-list"
              ref="listContainer"
              @scroll="onScroll"
            >
              <!-- Empty state -->
              <div v-if="filteredProtocolos.length === 0" class="empty-state">
                <v-icon size="36" color="#d1d5db">mdi-folder-search-outline</v-icon>
                <p class="empty-title">Sin resultados</p>
                <p class="empty-sub">{{ searchText ? 'Intenta con otro término de búsqueda' : 'No hay protocolos disponibles' }}</p>
              </div>

              <!-- Protocol items (only render visibleProtocolos) -->
              <div
                v-for="protocolo in visibleProtocolos"
                :key="protocolo.id"
                class="protocol-row"
                :class="{ selected: selectedProtocolos.includes(protocolo.id) }"
                @click="toggleProtocolo(protocolo)"
              >
                <div
                  class="custom-checkbox"
                  :class="{ checked: selectedProtocolos.includes(protocolo.id) }"
                >
                  <v-icon v-if="selectedProtocolos.includes(protocolo.id)" size="11" color="white">mdi-check</v-icon>
                </div>
                <div class="protocol-info">
                  <span class="protocol-etapa">{{ protocolo.etapa }}</span>
                  <span class="protocol-title">{{ protocolo.title }}</span>
                </div>
              </div>

              <!-- Load more indicator -->
              <div v-if="isLoadingMore" class="load-more-indicator">
                <v-progress-circular indeterminate color="#5e85ef" size="20" width="2"></v-progress-circular>
              </div>

              <!-- End of list indicator -->
              <div v-if="!isLoadingMore && visibleProtocolos.length > 0 && visibleProtocolos.length === filteredProtocolos.length && filteredProtocolos.length > pageSize" class="end-indicator">
                <span>· · ·</span>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Fixed footer -->
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">
            Cancelar
          </button>
          <button
            class="btn-import"
            :class="{ disabled: selectedProtocolos.length === 0 || importing }"
            :disabled="selectedProtocolos.length === 0 || importing"
            @click="importProtocolos"
          >
            <v-progress-circular v-if="importing" indeterminate color="white" size="14" width="2" class="mr-2"></v-progress-circular>
            <v-icon v-else size="15" class="mr-1">mdi-import</v-icon>
            {{ importing ? 'Importando...' : 'Importar' }}
            <span v-if="selectedProtocolos.length > 0 && !importing" class="import-count">{{ selectedProtocolos.length }}</span>
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { copyProtocolsToProject, getAllProtocolos } from '@/helpers/api/entrega';

const PAGE_SIZE = 30;

export default {
  name: 'ImportarProtocolosModal',
  props: {
    closeModal: {
      type: Function,
      required: true
    },
    modalStatus: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    loading: false,
    importing: false,
    isLoadingMore: false,
    availableProtocolos: [],
    selectedProtocolos: [],
    searchText: '',
    visibleCount: PAGE_SIZE,
    pageSize: PAGE_SIZE,
  }),
  computed: {
    filteredProtocolos() {
      if (!this.searchText) return this.availableProtocolos;
      const search = this.searchText.toLowerCase();
      return this.availableProtocolos.filter(p =>
        p.title.toLowerCase().includes(search) ||
        p.etapa.toLowerCase().includes(search)
      );
    },
    visibleProtocolos() {
      return this.filteredProtocolos.slice(0, this.visibleCount);
    },
    isAllSelected() {
      return this.filteredProtocolos.length > 0 &&
        this.filteredProtocolos.every(p => this.selectedProtocolos.includes(p.id));
    },
    isIndeterminate() {
      return this.selectedProtocolos.length > 0 && !this.isAllSelected &&
        this.filteredProtocolos.some(p => this.selectedProtocolos.includes(p.id));
    }
  },
  watch: {
    modalStatus(newVal) {
      if (newVal) {
        this.loadAvailableProtocolos();
        this.selectedProtocolos = [];
        this.searchText = '';
        this.visibleCount = PAGE_SIZE;
      }
    }
  },
  methods: {
    onSearchInput() {
      this.visibleCount = PAGE_SIZE;
      this.$nextTick(() => {
        if (this.$refs.listContainer) {
          this.$refs.listContainer.scrollTop = 0;
        }
      });
    },

    onScroll(e) {
      const el = e.target;
      const threshold = 100;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

      if (nearBottom && !this.isLoadingMore && this.visibleCount < this.filteredProtocolos.length) {
        this.loadMoreItems();
      }
    },

    loadMoreItems() {
      this.isLoadingMore = true;
      setTimeout(() => {
        this.visibleCount = Math.min(
          this.visibleCount + this.pageSize,
          this.filteredProtocolos.length
        );
        this.isLoadingMore = false;
      }, 200);
    },

    async loadAvailableProtocolos() {
      this.loading = true;
      try {
        const response = await getAllProtocolos({ page: 1 });
        if (response && response.code === 200) {
          this.availableProtocolos = response.protocolos;
        } else {
          this.availableProtocolos = [];
          this.showError('Error al cargar los protocolos disponibles');
        }
      } catch (error) {
        console.error('Error loading available protocols:', error);
        this.availableProtocolos = [];
        this.showError('Error al cargar los protocolos disponibles');
      } finally {
        this.loading = false;
      }
    },

    toggleProtocolo(protocolo) {
      const index = this.selectedProtocolos.indexOf(protocolo.id);
      if (index > -1) {
        this.selectedProtocolos.splice(index, 1);
      } else {
        this.selectedProtocolos.push(protocolo.id);
      }
    },

    toggleAll() {
      if (this.isAllSelected) {
        // Deselect all filtered
        const filteredIds = this.filteredProtocolos.map(p => p.id);
        this.selectedProtocolos = this.selectedProtocolos.filter(id => !filteredIds.includes(id));
      } else {
        // Select all filtered (merge with existing)
        const filteredIds = this.filteredProtocolos.map(p => p.id);
        const newIds = filteredIds.filter(id => !this.selectedProtocolos.includes(id));
        this.selectedProtocolos = [...this.selectedProtocolos, ...newIds];
      }
    },

    async importProtocolos() {
      if (this.selectedProtocolos.length === 0) return;
      this.importing = true;
      try {
        const projectId = this.$store.projectsStore.state.selectedProject.id;
        const response = await copyProtocolsToProject({
          project_id: projectId,
          protocol_ids: this.selectedProtocolos
        });

        if (response && response.code === 200) {
          this.showSuccess(`Se importaron ${this.selectedProtocolos.length} protocolo(s) exitosamente`);
          this.closeModal();
          this.$store.entregaStore.dispatch('getProtocolos', { project_id: projectId });
        } else {
          this.showError('Error al importar los protocolos');
        }
      } catch (error) {
        console.error('Error importing protocols:', error);
        this.showError('Error al importar los protocolos');
      } finally {
        this.importing = false;
      }
    },

    showSuccess(message) {
      this.$store.globalStore.dispatch('addMessage', {
        message, title: 'Éxito', created: new Date(),
        type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3'
      });
    },

    showError(message) {
      this.$store.globalStore.dispatch('addMessage', {
        message, title: 'Error', created: new Date(),
        type: 'error', icon: 'mdi-alert-circle', color: '#fb8c00'
      });
    }
  }
};
</script>

<style scoped>
/* Card */
.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 600px;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: linear-gradient(135deg, #5e85ef, #3d6de0);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.close-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: background 0.15s, color 0.15s;
}
.close-btn:hover {
  background: #f1f5f9;
  color: #475569;
}

/* Body */
.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 12px;
}
.loading-text {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}

/* Search */
.search-section {
  padding: 12px 16px 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.search-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 10px;
  height: 34px;
  transition: border-color 0.15s;
}
.search-input-wrapper:focus-within {
  border-color: #5e85ef;
  background: #fff;
}

.search-icon { flex-shrink: 0; }

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #334155;
}
.search-input::placeholder { color: #b0bac8; }

.clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

.results-count {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
}

/* Select all */
.select-all-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px 6px 14px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
  flex-shrink: 0;
}

.select-all-label {
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  user-select: none;
}

.count-chip {
  font-size: 11px !important;
}

/* Custom checkbox */
.custom-checkbox {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid #cbd5e1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  cursor: pointer;
}
.custom-checkbox.checked {
  background: #5e85ef;
  border-color: #5e85ef;
}
.custom-checkbox.indeterminate {
  background: #5e85ef;
  border-color: #5e85ef;
}

/* Protocols list */
.protocols-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: 360px;
  padding: 4px 0;
}

.protocols-list::-webkit-scrollbar { width: 4px; }
.protocols-list::-webkit-scrollbar-track { background: transparent; }
.protocols-list::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
.protocols-list::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }

/* Protocol row */
.protocol-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 16px 7px 14px;
  cursor: pointer;
  transition: background 0.1s;
  border-bottom: 1px solid transparent;
}
.protocol-row:hover {
  background: #f8fafc;
}
.protocol-row.selected {
  background: #eff4ff;
}
.protocol-row.selected:hover {
  background: #e5edff;
}

.protocol-info {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.protocol-etapa {
  font-size: 11px;
  font-weight: 600;
  color: #5e85ef;
  background: #eff4ff;
  padding: 1px 7px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: 0.01em;
}

.protocol-title {
  font-size: 13px;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* Load more */
.load-more-indicator {
  display: flex;
  justify-content: center;
  padding: 12px;
}

.end-indicator {
  text-align: center;
  padding: 8px;
  color: #cbd5e1;
  font-size: 14px;
  letter-spacing: 4px;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 6px;
}
.empty-title {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}
.empty-sub {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0 16px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-cancel:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #475569;
}

.btn-import {
  display: flex;
  align-items: center;
  padding: 0 16px;
  height: 34px;
  border-radius: 7px;
  border: none;
  background: linear-gradient(135deg, #5e85ef, #3d6de0);
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s;
  gap: 4px;
  box-shadow: 0 1px 4px rgba(94, 133, 239, 0.35);
}
.btn-import:hover:not(.disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(94, 133, 239, 0.45);
}
.btn-import.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.import-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: rgba(255,255,255,0.25);
  font-size: 11px;
  font-weight: 700;
  padding: 0 5px;
  margin-left: 2px;
}
</style>